import { MouseEvent, useState } from "react"
import { Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLock, faLockOpen } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import {
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrdersView,
} from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import {
    changeLockStatusMerchant,
    MerchantMeetingServiceOrdersQuery,
} from "../../../modules/merchant/merchant/production/meetingService/meetingServiceApi"
import { getHiddenOrderStatuses } from "../../../modules/merchant/merchant/production/meetingService/meetingServiceLib"
import { useMeetingServiceStatusUndo } from "../../../modules/merchant/merchant/production/meetingService/useMeetingServiceStatusUndo"
import { MeetingServiceOrdersDependencies } from "../../../modules/merchant/merchant/production/meetingService/components/OrderDeliveryList"
import {
    MinimizedStatusButton,
    StatusButton,
} from "../../../modules/merchant/merchant/production/meetingService/components/StatusMenu"
import { StatusChangeModal } from "../../../modules/merchant/merchant/production/meetingService/components/StatusChangeModal"
import { StandardDialog } from "../dialogs/StandardDialog"
import { StandardButton } from "../buttons/StandardButton"

const useStyles = makeStyles()((theme) => ({
    footer: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    lockButton: {
        "&.MuiButton-root": {
            height: "3rem",
            borderRadius: ".75rem",
        },
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    view: MeetingServiceOrdersView
    meetingServiceDependencies: MeetingServiceOrdersDependencies
}>

export function MerchantOrderFooter({ order, view, meetingServiceDependencies }: Props) {
    const { classes } = useStyles()
    const showStatusUndo = useMeetingServiceStatusUndo()

    const isLocked = !!order.deliveryDetails.isLocked
    const { mutate: updateLock } = useApiMutation(changeLockStatusMerchant, {
        onSuccess: (_params, _data, context) =>
            context.queryClient.invalidateQueries({ queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE] }),
    })
    const toggleLock = () => updateLock({ updateLockStatus: { isLocked: !isLocked, orderIds: [order.id] } })

    const hiddenStatuses = getHiddenOrderStatuses(view)
    const [statusAnchor, setStatusAnchor] = useState<{ top: number; left: number } | null>(null)
    const [pendingStatus, setPendingStatus] = useState<FilteredStatuses | null>(null)
    const allowedOrderIds =
        pendingStatus &&
        (order.orderAction.statusTransitions?.allowed?.[pendingStatus] ||
            !order.orderAction.statusTransitions?.disallowed?.[pendingStatus])
            ? [order.id]
            : []

    const onStatusClick = (event: MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setStatusAnchor((prev) => (prev ? null : { top: rect.top, left: rect.right }))
    }

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing="1rem"
                className={classes.footer}
            >
                <LocalizedStrict
                    id={
                        isLocked
                            ? "meeting-service-production-filter-locked-locked"
                            : "meeting-service-production-filter-locked-unlocked"
                    }
                >
                    <StandardButton
                        fullWidth={false}
                        variant="outlined"
                        color="grey"
                        className={classes.lockButton}
                        startIcon={<FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />}
                        onClick={toggleLock}
                    >
                        {isLocked ? "Locked" : "Unlocked"}
                    </StandardButton>
                </LocalizedStrict>
                <MinimizedStatusButton
                    status={order.deliveryDetails.statusDetails.status}
                    tooltipLabel={order.deliveryDetails.statusDetails.message}
                    onClick={onStatusClick}
                />
            </Stack>

            <StatusButton
                currentStatus={order.deliveryDetails.statusDetails.status}
                hiddenStatuses={hiddenStatuses}
                anchorEl={statusAnchor}
                onStatusChange={(status) => setPendingStatus(status)}
                handleClose={() => setStatusAnchor(null)}
            />

            {pendingStatus && (
                <LocalizedStrict id="meeting-service-batch-modal-title" attrs={{ dialogTitle: true }}>
                    <StandardDialog open onClose={() => setPendingStatus(null)} dialogTitle="" maxWidthProp="lg">
                        <StatusChangeModal
                            setShowUndo={(orderCount) =>
                                showStatusUndo({ status: pendingStatus, orderIds: [order.id], orderCount })
                            }
                            onClose={() => setPendingStatus(null)}
                            selectedStatus={pendingStatus}
                            allowedOrderIds={allowedOrderIds}
                            orders={[order]}
                            meetingServiceDependencies={meetingServiceDependencies}
                        />
                    </StandardDialog>
                </LocalizedStrict>
            )}
        </>
    )
}
